import React, {useCallback, useEffect, useState} from 'react';
import {View, ScrollView, StyleSheet, RefreshControl, Alert} from 'react-native';
import {
  Text, Card, Chip, Appbar, FAB, ActivityIndicator,
  Dialog, Portal, Button, TextInput, Checkbox, IconButton,
} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation} from '@react-navigation/native';
import {roleApi} from '../../services/api/roleApi';
import {colors, spacing} from '../../theme';

const RoleManagementScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();

  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedRole, setExpandedRole] = useState(null);

  const [addVisible, setAddVisible] = useState(false);
  const [newName, setNewName] = useState('');
  const [newCode, setNewCode] = useState('');
  const [selectedPerms, setSelectedPerms] = useState(new Set());

  const [editRole, setEditRole] = useState(null);
  const [editName, setEditName] = useState('');
  const [editPerms, setEditPerms] = useState(new Set());

  const fetchData = useCallback(async () => {
    try {
      const [rolesRes, permsRes] = await Promise.all([
        roleApi.list(),
        roleApi.listPermissions(),
      ]);
      setRoles(rolesRes.data.data || []);
      setPermissions(permsRes.data.data || []);
    } catch (e) {
      Alert.alert(t('common.error'), e.response?.data?.error?.message || e.message);
    }
  }, [t]);

  useEffect(() => {
    setLoading(true);
    fetchData().finally(() => setLoading(false));
  }, [fetchData]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  }, [fetchData]);

  const handleAddRole = useCallback(async () => {
    if (!newName.trim() || !newCode.trim()) return;
    try {
      await roleApi.create({
        name: newName.trim(),
        code: newCode.trim().toUpperCase(),
        permissionIds: Array.from(selectedPerms),
      });
      setAddVisible(false);
      setNewName('');
      setNewCode('');
      setSelectedPerms(new Set());
      await fetchData();
    } catch (e) {
      Alert.alert(t('common.error'), e.response?.data?.error?.message || e.message);
    }
  }, [newName, newCode, selectedPerms, fetchData, t]);

  const togglePerm = useCallback((permId, isEdit) => {
    const setter = isEdit ? setEditPerms : setSelectedPerms;
    setter(prev => {
      const next = new Set(prev);
      if (next.has(permId)) next.delete(permId);
      else next.add(permId);
      return next;
    });
  }, []);

  const openEdit = useCallback(role => {
    setEditRole(role);
    setEditName(role.name);
    setEditPerms(new Set((role.permissions || []).map(p => p.id)));
  }, []);

  const handleEditRole = useCallback(async () => {
    if (!editRole || !editName.trim()) return;
    try {
      await roleApi.update(editRole.id, {name: editName.trim()});
      await roleApi.updatePermissions(editRole.id, {permissionIds: Array.from(editPerms)});
      setEditRole(null);
      await fetchData();
    } catch (e) {
      Alert.alert(t('common.error'), e.response?.data?.error?.message || e.message);
    }
  }, [editRole, editName, editPerms, fetchData, t]);

  const handleDeleteRole = useCallback(role => {
    Alert.alert(
      t('common.confirm'),
      t('role.deleteConfirm', {name: role.name}),
      [
        {text: t('common.cancel'), style: 'cancel'},
        {
          text: t('common.delete'),
          style: 'destructive',
          onPress: async () => {
            try {
              await roleApi.delete(role.id);
              await fetchData();
            } catch (e) {
              Alert.alert(t('common.error'), e.response?.data?.error?.message || e.message);
            }
          },
        },
      ],
    );
  }, [fetchData, t]);

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.BackAction onPress={() => navigation.goBack()} color={colors.white} />
        <Appbar.Content title={t('role.title')} titleStyle={styles.appbarTitle} />
      </Appbar.Header>

      <ScrollView
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} colors={[colors.primary]} />}>
        {roles.map(role => (
          <Card
            key={role.id}
            style={styles.roleCard}
            onPress={() => setExpandedRole(expandedRole === role.id ? null : role.id)}>
            <Card.Content>
              <View style={styles.roleHeader}>
                <View style={styles.roleInfo}>
                  <Text variant="titleMedium" style={styles.roleName}>{role.name}</Text>
                  <Text variant="bodySmall" style={styles.roleCode}>{role.code}</Text>
                </View>
                {!role.isSystem && (
                  <View style={styles.actionButtons}>
                    <IconButton icon="pencil" size={18} iconColor={colors.primary}
                      onPress={() => openEdit(role)} style={styles.actionBtn} />
                    <IconButton icon="delete" size={18} iconColor={colors.error}
                      onPress={() => handleDeleteRole(role)} style={styles.actionBtn} />
                  </View>
                )}
                <Chip style={styles.countChip}>
                  <Text style={{fontSize: 13, color: '#000', marginTop: 3}}>{(role.permissions || []).length} permissions</Text>
                </Chip>
              </View>
              {role.isSystem && (
                <Chip style={styles.systemChip}><Text style={{fontSize: 13, color: '#000', marginTop: 3}}>System</Text></Chip>
              )}
              {expandedRole === role.id && (
                <View style={styles.permList}>
                  {(role.permissions || []).map(p => (
                    <Chip key={p.id} style={styles.permChip}>
                      <Text style={{fontSize: 13, color: '#000', marginTop: 3}}>{p.code}</Text>
                    </Chip>
                  ))}
                </View>
              )}
            </Card.Content>
          </Card>
        ))}
        <View style={{height: 80}} />
      </ScrollView>

      <FAB icon="plus" style={styles.fab} onPress={() => setAddVisible(true)} color={colors.white} />

      <Portal>
        <Dialog visible={addVisible} onDismiss={() => setAddVisible(false)} style={{borderRadius: 15}}>
          <Dialog.Title>{t('role.addRole')}</Dialog.Title>
          <Dialog.ScrollArea style={styles.dialogScroll}>
            <ScrollView>
              <TextInput label={t('role.roleName')} value={newName} onChangeText={setNewName}
                mode="outlined" style={styles.dialogInput} />
              <TextInput label="Code" value={newCode} onChangeText={setNewCode}
                mode="outlined" style={styles.dialogInput} autoCapitalize="characters" />
              <Text variant="titleSmall" style={styles.permTitle}>{t('role.selectPermissions')}</Text>
              {permissions.map(p => (
                <View key={p.id} style={styles.permItem}>
                  <Checkbox
                    status={selectedPerms.has(p.id) ? 'checked' : 'unchecked'}
                    onPress={() => togglePerm(p.id, false)}
                    color={colors.primary}
                  />
                  <Text variant="bodySmall" style={styles.permLabel}>{p.code}</Text>
                </View>
              ))}
            </ScrollView>
          </Dialog.ScrollArea>
          <Dialog.Actions>
            <Button onPress={() => setAddVisible(false)}>{t('common.cancel')}</Button>
            <Button onPress={handleAddRole}>{t('common.save')}</Button>
          </Dialog.Actions>
        </Dialog>

        <Dialog visible={!!editRole} onDismiss={() => setEditRole(null)} style={{borderRadius: 15}}>
          <Dialog.Title>{t('role.editRole')}</Dialog.Title>
          <Dialog.ScrollArea style={styles.dialogScroll}>
            <ScrollView>
              <TextInput label={t('role.roleName')} value={editName} onChangeText={setEditName}
                mode="outlined" style={styles.dialogInput} />
              <Text variant="titleSmall" style={styles.permTitle}>{t('role.selectPermissions')}</Text>
              {permissions.map(p => (
                <View key={p.id} style={styles.permItem}>
                  <Checkbox
                    status={editPerms.has(p.id) ? 'checked' : 'unchecked'}
                    onPress={() => togglePerm(p.id, true)}
                    color={colors.primary}
                  />
                  <Text variant="bodySmall" style={styles.permLabel}>{p.code}</Text>
                </View>
              ))}
            </ScrollView>
          </Dialog.ScrollArea>
          <Dialog.Actions>
            <Button onPress={() => setEditRole(null)}>{t('common.cancel')}</Button>
            <Button onPress={handleEditRole}>{t('common.save')}</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  roleCard: {marginHorizontal: spacing.md, marginTop: spacing.sm, backgroundColor: colors.surface, borderRadius: 8},
  roleHeader: {flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center'},
  roleInfo: {flex: 1},
  roleName: {fontWeight: '600', color: colors.text},
  roleCode: {color: colors.textSecondary},
  actionButtons: {flexDirection: 'row', marginRight: spacing.xs},
  actionBtn: {margin: 0},
  countChip: {backgroundColor: colors.primary + '15', height: 24, paddingVertical: 0},
  countText: {fontSize: 10, lineHeight: 14, color: colors.primary},
  systemChip: {alignSelf: 'flex-start', marginTop: spacing.xs, backgroundColor: colors.info + '20', height: 24, paddingVertical: 0},
  systemText: {fontSize: 10, lineHeight: 14, color: colors.info},
  permList: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginTop: spacing.sm},
  permChip: {backgroundColor: colors.background, height: 24, paddingVertical: 0},
  permText: {fontSize: 10, lineHeight: 14, color: colors.textSecondary},
  fab: {position: 'absolute', right: spacing.md, bottom: spacing.md, backgroundColor: colors.primary},
  dialogScroll: {maxHeight: 400},
  dialogInput: {marginBottom: spacing.sm, backgroundColor: colors.surface},
  permTitle: {marginTop: spacing.sm, marginBottom: spacing.xs, color: colors.text},
  permItem: {flexDirection: 'row', alignItems: 'center'},
  permLabel: {color: colors.text},
});

export default RoleManagementScreen;
